import { getFunctions, httpsCallable } from 'firebase/functions';
import { app } from '../config/firebaseConfig';
import { fetchJsonWithFallback } from './apiClient';

const billingFunctions = getFunctions(app, 'europe-west2');

const callBilling = async (name, payload = {}) => {
  const callable = httpsCallable(billingFunctions, name);
  const response = await callable(payload);
  return response?.data || null;
};

const isLocalhost = () => {
  if (typeof window === 'undefined') return false;
  const host = window.location?.hostname || '';
  return host === 'localhost' || host === '127.0.0.1' || host.endsWith('.local');
};

const toProductsList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.products)) return payload.products;
  return [];
};

const classifyCallableError = (error) => {
  const code = typeof error?.code === 'string' ? error.code : '';
  const message = typeof error?.message === 'string' ? error.message : '';
  const normalizedCode = code.toLowerCase();
  const normalizedMessage = message.toLowerCase();
  const missingCallable =
    normalizedCode.includes('not-found') ||
    normalizedCode.includes('not_found') ||
    normalizedMessage.includes('not found') ||
    normalizedMessage.includes('404');

  const authError =
    normalizedCode.includes('unauthenticated') ||
    normalizedCode.includes('permission-denied') ||
    normalizedCode.includes('permission_denied');

  const transportOrCorsError =
    normalizedMessage.includes('cors') ||
    normalizedMessage.includes('failed to fetch') ||
    normalizedMessage.includes('preflight') ||
    normalizedCode.includes('internal') ||
    normalizedCode.includes('unavailable') ||
    normalizedCode.includes('unknown');

  return {
    callableErrorCode: code || null,
    callableErrorMessage: message || null,
    missingCallable,
    authError,
    transportOrCorsError,
  };
};

const parseCallableCandidates = (value) => {
  if (!value || typeof value !== 'string') return [];
  return value
    .split(/[\s,]+/)
    .map((entry) => entry.trim())
    .filter(Boolean);
};

const dedupeList = (values = []) => {
  const seen = new Set();
  const out = [];
  values.forEach((value) => {
    const key = String(value || '').trim();
    if (!key) return;
    if (seen.has(key)) return;
    seen.add(key);
    out.push(key);
  });
  return out;
};

const callFirstAvailableCallable = async (candidateNames = [], payload = {}) => {
  const names = dedupeList(candidateNames);
  let lastError = null;
  let missingOnly = false;
  const attempts = [];

  for (const name of names) {
    try {
      const response = await callBilling(name, payload);
      return {
        callable: name,
        response,
        attempts,
      };
    } catch (error) {
      const classification = classifyCallableError(error);
      attempts.push({
        callable: name,
        ...classification,
      });
      if (classification.authError) {
        error.callable = name;
        error.callableAttempts = attempts;
        throw error;
      }
      if (classification.missingCallable) {
        missingOnly = true;
        lastError = error;
        continue;
      }
      if (classification.transportOrCorsError) {
        // Candidate may not exist (CORS/internal on direct function URL); try the next name.
        missingOnly = true;
        lastError = error;
        continue;
      }
      throw error;
    }
  }

  const err = lastError || new Error(`No callable candidates configured: ${names.join(', ')}`);
  err.missingCallable = missingOnly || Boolean(err?.missingCallable);
  err.callableCandidates = names;
  err.callableAttempts = attempts;
  throw err;
};

const fetchApiPayload = async (path, acceptStatuses = []) => {
  try {
    return await fetchJsonWithFallback(path, {
      acceptStatuses,
      handleAcceptedStatus: async () => null,
    });
  } catch (error) {
    console.warn(`[billingCallables] ${path} request failed`, error);
    return null;
  }
};

const fetchCatalogueFromApiRoute = async () => {
  const fromProducts = await fetchApiPayload('/api/products', [404]);
  const productsList = toProductsList(fromProducts);

  const hasSubscriptionFromProducts = productsList.some((plan) => {
    const key = String(plan?.slug || plan?.name || plan?.id || '').toLowerCase();
    const kind = String(plan?.kind || '').toLowerCase();
    return key !== 'payg' && kind !== 'payg';
  });

  if (hasSubscriptionFromProducts) {
    return {
      products: productsList,
      source: 'api-products',
      diagnostics: {
        apiProductsCount: productsList.length,
        apiCatalogCount: 0,
      },
    };
  }

  // Fallback to Firestore-backed catalog snapshot when /api/products returns PAYG-only.
  const fromCatalog = await fetchApiPayload('/api/catalog', [404]);
  const catalogProducts = toProductsList(fromCatalog);

  if (catalogProducts.length > 0) {
    return {
      products: catalogProducts,
      source: 'api-catalog',
      diagnostics: {
        apiProductsCount: productsList.length,
        apiCatalogCount: catalogProducts.length,
      },
    };
  }

  return {
    products: productsList,
    source: 'api-products',
    diagnostics: {
      apiProductsCount: productsList.length,
      apiCatalogCount: catalogProducts.length,
    },
  };
};

export const billingCreateCheckoutSession = async (payload = {}) => {
  return await callBilling('billingCreateCheckoutSession', {
    ...payload,
    origin: typeof window !== 'undefined' ? window.location.origin : undefined,
  });
};

export const billingCreatePortalSession = async (payload = {}) => {
  return await callBilling('billingCreatePortalSession', payload);
};

export const redeemPromoCode = async (payload = {}) => {
  return callBilling('redeemPromoCode', payload);
};

export const startLaunchPilotTrial = async (payload = {}) => {
  return callBilling('startLaunchPilotTrial', payload);
};

export const billingGetStatus = async (payload = {}) => {
  return await callBilling('billingGetStatus', payload);
};

export const billingSwitchToPayg = async (payload = {}) => {
  const envCandidates = parseCallableCandidates(
    process.env.REACT_APP_BILLING_SWITCH_PAYG_CALLABLES ||
    process.env.REACT_APP_BILLING_SWITCH_PAYG_CALLABLE ||
    ''
  );
  const candidates = dedupeList([
    ...envCandidates,
    'billingSwitchToPayg',
    'billingSwitchPlan',
    'billingChangePlan',
  ]);

  const { callable, response } = await callFirstAvailableCallable(candidates, payload);
  if (response?.ok === false) {
    const error = new Error(response?.error || 'Failed to switch to PAYG');
    error.data = response;
    error.callable = callable;
    throw error;
  }
  return {
    ...(response && typeof response === 'object' ? response : {}),
    ok: response?.ok !== false,
    source: 'callable',
    callable,
  };
};

export const billingChangePlan = async (payload = {}) => {
  const envCandidates = parseCallableCandidates(
    process.env.REACT_APP_BILLING_CHANGE_PLAN_CALLABLES ||
    process.env.REACT_APP_BILLING_CHANGE_PLAN_CALLABLE ||
    ''
  );
  const candidates = dedupeList([
    ...envCandidates,
    'billingChangePlan',
    'billingSwitchPlan',
    'billingUpdateSubscriptionPlan',
  ]);

  const { callable, response } = await callFirstAvailableCallable(candidates, payload);
  if (response?.ok === false) {
    const error = new Error(response?.error || 'Failed to change plan');
    error.data = response;
    error.callable = callable;
    throw error;
  }
  return {
    ...(response && typeof response === 'object' ? response : {}),
    ok: response?.ok !== false,
    source: 'callable',
    callable,
  };
};

export const billingListCatalog = async (payload = {}) => {
  // Local development should prefer same-origin API routes to avoid direct callable CORS/preflight issues.
  if (isLocalhost()) {
    const apiFallback = await fetchCatalogueFromApiRoute();
    return {
      ...apiFallback,
      diagnostics: {
        ...(apiFallback?.diagnostics || {}),
        callableAttempted: false,
        callableFailed: false,
        missingCallable: false,
      },
    };
  }

  try {
    const response = await callBilling('billingListCatalog', payload);
    const products = toProductsList(response);
    return {
      ...(response && typeof response === 'object' ? response : {}),
      products,
      source: response?.source || 'callable',
      diagnostics: {
        callableAttempted: true,
        callableFailed: false,
        missingCallable: false,
      },
    };
  } catch (error) {
    // Fallback to API route so plan UIs still work if callable is unavailable/cross-origin blocked.
    console.warn('[billingCallables] billingListCatalog callable failed; falling back to /api/products', error);
    const apiFallback = await fetchCatalogueFromApiRoute();
    const classification = classifyCallableError(error);
    return {
      ...apiFallback,
      diagnostics: {
        ...(apiFallback?.diagnostics || {}),
        callableAttempted: true,
        callableFailed: true,
        ...classification,
      },
    };
  }
};

const coerceMetadata = (value) =>
  value && typeof value === 'object' && !Array.isArray(value) ? value : {};

const normalizePriceInterval = (price) => {
  if (typeof price?.interval === 'string' && price.interval.trim()) {
    return price.interval.trim().toLowerCase();
  }
  const recurringInterval =
    typeof price?.recurring?.interval === 'string' ? price.recurring.interval : '';
  return recurringInterval.trim().toLowerCase();
};

const isRecurringCatalogPrice = (price) => {
  const id = typeof price?.id === 'string' ? price.id : typeof price?.priceId === 'string' ? price.priceId : '';
  const interval = normalizePriceInterval(price);
  const active = price?.active !== false;
  return Boolean(id && id.startsWith('price_') && interval && active);
};

const normalizeDirectPlans = (products = []) =>
  products
    .map((plan) => {
      const rawPrices = Array.isArray(plan?.prices) ? plan.prices : [];
      const prices = rawPrices.filter(isRecurringCatalogPrice);
      return {
        ...plan,
        prices,
      };
    })
    .filter((plan) => {
      const planId = String(plan?.id || '').trim();
      const prices = Array.isArray(plan?.prices) ? plan.prices : [];
      return Boolean(planId && prices.length > 0);
    });

const normalizePlansFromCatalogResponse = (response) => {
  const products = Array.isArray(response?.products) ? response.products : [];

  // If API /products already returns normalized plans, use them directly.
  if (response?.source === 'api-products') {
    return normalizeDirectPlans(products);
  }

  return products
    .map((product) => {
      const metadata = coerceMetadata(product?.metadata);
      const rawPrices = Array.isArray(product?.prices) ? product.prices : [];
      const prices = rawPrices
        .map((price) => {
          const interval = typeof price?.interval === 'string' ? price.interval : null;
          const unitAmount =
            typeof price?.unitAmount === 'number' && Number.isFinite(price.unitAmount)
              ? price.unitAmount
              : typeof price?.unit_amount === 'number' && Number.isFinite(price.unit_amount)
                ? price.unit_amount
              : null;
          return {
            id:
              typeof price?.priceId === 'string'
                ? price.priceId
                : typeof price?.id === 'string'
                  ? price.id
                  : '',
            currency: typeof price?.currency === 'string' ? price.currency : 'gbp',
            unit_amount: unitAmount,
            recurring: interval ? { interval, interval_count: 1 } : null,
            interval,
            interval_count: interval ? 1 : null,
            type: 'recurring',
            active: price?.active !== false,
            metadata: coerceMetadata(price?.metadata),
          };
        })
        .filter((price) => isRecurringCatalogPrice(price));

      const summaryFeaturesRaw =
        typeof metadata.summaryFeatures === 'string'
          ? metadata.summaryFeatures
          : typeof metadata.summary_features === 'string'
            ? metadata.summary_features
            : '';

      return {
        id:
          typeof product?.productId === 'string'
            ? product.productId
            : typeof product?.id === 'string'
              ? product.id
              : '',
        name: typeof product?.name === 'string' ? product.name : '',
        description: typeof product?.description === 'string' ? product.description : '',
        tagline:
          typeof metadata.tagline === 'string'
            ? metadata.tagline
            : typeof metadata.subtitle === 'string'
              ? metadata.subtitle
              : '',
        summaryFeatures: summaryFeaturesRaw
          .split('|')
          .map((item) => item.trim())
          .filter(Boolean),
        customPricing:
          metadata.customPricing === 'true' ||
          metadata.customPricing === true ||
          metadata.custom_pricing === 'true' ||
          metadata.custom_pricing === true,
        active: product?.active !== false,
        kind: String(product?.productId || '').toLowerCase() === 'payg' ? 'payg' : 'subscription',
        metadata,
        prices,
      };
    })
    .filter((plan) => plan.id && Array.isArray(plan.prices) && plan.prices.length > 0);
};

export const billingListCatalogPlansDetailed = async (payload = {}) => {
  const response = await billingListCatalog(payload);
  const plans = normalizePlansFromCatalogResponse(response);
  return {
    plans,
    diagnostics: {
      ...(response?.diagnostics || {}),
      source: response?.source || null,
      catalogEmpty: plans.length === 0,
      planCount: plans.length,
    },
  };
};

export const billingListCatalogPlans = async (payload = {}) => {
  const detailed = await billingListCatalogPlansDetailed(payload);
  return detailed.plans;
};

export const getEntitlementCatalog = async () => {
  try {
    return await callBilling('getEntitlementCatalog', {});
  } catch (error) {
    console.warn('[billingCallables] getEntitlementCatalog callable failed', error);
    return null;
  }
};
